<template>
    <div v-if="rows">
        <div v-if="loading">
            <Skeleton class="ml-4 mt-4" style="max-width: 90vw" height="6rem" borderRadius="10px"/>
        </div>
        <div v-else-if="rows.data.length === 0 || Object.keys(rows.total).length === 0"
             class="d-flex justify-content-center mt-4">
            <div class="text-center flex-column kt-font-lg">
                <div class="font-weight-bold kt-font-xl">
                    {{ $t('AppWorkAnalyzerTable.Sem dados') }}
                </div>
                <div v-if="!allFiltersEmpty">
                    {{ $t('AppWorkAnalyzerTable.Altere o filtro e tente novamente') }}
                </div>
            </div>
        </div>
        <div v-else class="work-analyzer-table">
            <AppWorkAnalyzerHarvestTable v-if="operationType.name === 'HARVEST'" :data="rows.data" :pivot="pivot"
                                         :view="tableView" :total="rows.total" :drill-in-string="drillInString"
                                         :pdf="isPdf" @goToFieldAnalysis="goToFieldAnalysis" @drillIn="drillIn"
                                         @setPivot="setPivot"/>
            <AppWorkAnalyzerTillageTable v-else-if="operationType.name === 'TILLAGE'" :data="rows.data" :pivot="pivot"
                                         :view="tableView" :total="rows.total" :drill-in-string="drillInString"
                                         :pdf="isPdf" @goToFieldAnalysis="goToFieldAnalysis" @drillIn="drillIn"
                                         @setPivot="setPivot"/>
            <AppWorkAnalyzerMowingTable v-else-if="operationType.name === 'MOWING'" :data="rows.data" :pivot="pivot"
                                        :view="tableView" :total="rows.total" :drill-in-string="drillInString"
                                        :pdf="isPdf" @goToFieldAnalysis="goToFieldAnalysis" @drillIn="drillIn"
                                        @setPivot="setPivot"/>
            <AppWorkAnalyzerSeedingTable v-else-if="operationType.name === 'SEEDING'" :data="rows.data" :pivot="pivot"
                                         :view="tableView" :total="rows.total" :drill-in-string="drillInString"
                                         :pdf="isPdf" @goToFieldAnalysis="goToFieldAnalysis" @drillIn="drillIn"
                                         @setPivot="setPivot"/>
            <AppWorkAnalyzerTransportTable v-else-if="operationType.name === 'TRANSPORT'" :data="rows.data" :pivot="pivot"
                                         :view="tableView" :total="rows.total" :drill-in-string="drillInString"
                                         :pdf="isPdf" @goToFieldAnalysis="goToFieldAnalysis" @drillIn="drillIn"
                                         @setPivot="setPivot"/>
            <AppWorkAnalyzerApplicationTable
                v-else-if="operationType.name === 'DRY_APPLICATION' || operationType.name === 'LIQUID_APPLICATION'"
                :data="rows.data" :view="tableView" :total="rows.total" :pivot="pivot" :type="operationType.name"
                :pdf="isPdf" :drill-in-string="drillInString" @drillIn="drillIn"
                @goToFieldAnalysis="goToFieldAnalysis" @setPivot="setPivot"/>
        </div>
    </div>
</template>

<script>

import Skeleton from "primevue/skeleton";

import dateFormat from "@/mixins/DateMixin";
import exportJsonToCsv from "@/mixins/JsonToCsvMixin";
import getTimeFromSecondsDurationFormatMixinShowZero from "@/mixins/DateMixin";
import {toCsvDecimalHours} from "@/utils/hourFormat";

import AppWorkAnalyzerApplicationTable from "./table/AppWorkAnalyzerApplicationTable.vue";
import AppWorkAnalyzerHarvestTable from "./table/AppWorkAnalyzerHarvestTable.vue";
import AppWorkAnalyzerSeedingTable from "./table/AppWorkAnalyzerSeedingTable.vue";
import AppWorkAnalyzerTillageTable from "./table/AppWorkAnalyzerTillageTable.vue";
import AppWorkAnalyzerMowingTable from "./table/AppWorkAnalyzerMowingTable.vue";
import AppWorkAnalyzerTransportTable from "./table/AppWorkAnalyzerTransportTable.vue";

export default {
    mixins: [dateFormat, getTimeFromSecondsDurationFormatMixinShowZero, exportJsonToCsv],
    data() {
        return {
            rows: {
                data: [],
                total: {}
            },
            formattedData: null
        }
    },
    methods: {
        formatRows(rows) {
            rows.forEach(row => this.formatRow(row));

            return rows;
        },
        formatRow(row) {
            row.area = this.parseNumericField(row.area, 2, 'ha');
            row.time_decimal = toCsvDecimalHours(row.time);
            row.time = this.getTimeFromSecondsDurationFormatMixinShowZero(row.time * 3600);
            row.fuel = this.parseNumericField(row.fuel, 2, 'L');
            row.fuel_rate = this.parseNumericField(row.fuel_rate, 2, 'L/h');
            row.speed = this.parseNumericField(row.speed, 2, 'km/h');
            row.productivity = this.parseNumericField(row.productivity, 2, 'ha/h');
            row.fuel_productivity = this.parseNumericField(row.fuel_productivity, 2, 'L/ha');
            row.fields = this.parseObject(row.fields, 'fields');
            row.vehicles = this.parseObject(row.vehicles, 'vehicles');
            row.operations = this.parseObject(row.operations, 'operations');
            row.operators = this.parseObject(row.operators, 'operators');

            switch (this.operationType.name) {
                case 'DRY_APPLICATION':
                case 'LIQUID_APPLICATION':
                    this.parseApplicationFields(row, this.operationType.name)
                    break;
                case 'TILLAGE':
                    this.parseTillageFields(row)
                    break;
                case 'HARVEST':
                    this.parseHarvestFields(row)
                    break;
                case 'SEEDING':
                    this.parseSeedingFields(row)
                    break;
                case 'TRANSPORT':
                    break;
            }

            return row
        },
        isInvalidValue(val) {
            return val === undefined || val === null || val === 'Infinity' || val === 'NaN';
        },
        parseObject(obj, column) {
            let formattedObj = {}
            let count = obj.count;

            formattedObj.total = count + ' ' + this.$t(`AppWorkAnalyzerTable.${column} ${count > 1 ? '(p)' : '(s)'}`)
            formattedObj.label = count > 1 ? count + ' ' + this.$t(`AppWorkAnalyzerTable.${column} (p)`) :
                obj.grouped_items[0].label === 'UNKNOWN' ? this.$t(`AppWorkAnalyzerTable.UNKNOWN`) : obj.grouped_items[0].label

            if (count > 1 && column === 'operations') {
                obj.grouped_items.forEach(operation => {
                    operation.label = this.parseDates(operation.label)
                });
            }

            formattedObj.tooltip = count > 1 ? this.buildTooltip(obj.grouped_items) : {disabled: true}

            if (count === 1) {
                formattedObj.id = obj.grouped_items[0].id
            }

            return formattedObj;
        },
        buildTooltip(values) {
            let text = ''
            let len = values.length;

            for (let v in values) {
                if (v === '10' && len > 10) {
                    text += `+${len - 10}`
                    return text;
                }

                if (v <= 10) {
                    let label = values[v].label === 'UNKNOWN' ? this.$t(`AppWorkAnalyzerTable.UNKNOWN`) : values[v].label
                    text += `${label}\n`
                }
            }

            return text
        },
        parseNumericField(val, decimals, unit) {
            if (this.isInvalidValue(val) || typeof val !== 'number') return 'N/D';
            return `${val.toFixed(decimals)} ${unit}`.trim();
        },
        parseApplicationFields(row, opType) {
            let unit = this.operationType.name === 'DRY_APPLICATION' ? 'T' : 'L'
            row.applied_total = this.parseNumericField(row.applied_total, 2, unit);
            if (opType === 'LIQUID_APPLICATION') {
                row.target_pressure = this.parseNumericField(row.target_pressure, 2, 'kPa');
                row.actual_pressure = this.parseNumericField(row.actual_pressure, 2, 'kPa');
            }
            row.target_total = this.parseNumericField(row.target_total, 2, unit);
            row.average_rate = this.parseNumericField(row.average_rate, 2, `${unit}/ha`);
            row.target_rate = this.parseNumericField(row.target_rate, 2, `${unit}/ha`);
            row.products = this.parseObject(row.products, 'products');
        },
        parseTillageFields(row) {
            row.target_depth = this.parseNumericField(row.target_depth, 2, 'cm');
            row.actual_depth = this.parseNumericField(row.actual_depth, 2, 'cm');
        },
        parseHarvestFields(row) {
            row.average_moisture = this.parseNumericField(row.average_moisture, 2, '%');
            row.total_dry_yield = this.parseNumericField(row.total_dry_yield, 2, 'm³');
            row.average_dry_yield = this.parseNumericField(row.average_dry_yield, 2, 'm³/ha');
            row.total_wet_weight = this.parseNumericField(row.total_wet_weight, 2, 't');
            row.average_wet_weight = this.parseNumericField(row.average_wet_weight, 2, 't/ha');
            row.throughput_dry = this.parseNumericField(row.throughput_dry, 2, 'm³/h');
            row.throughput_wet = this.parseNumericField(row.throughput_wet, 2, 't/h');
            row.gross_mass_productivity = this.parseNumericField(row.gross_mass_productivity, 2, 't/h');
            row.dry_matter = this.parseNumericField(row.dry_matter, 2, '%');
            row.fuel_efficiency = this.parseNumericField(row.fuel_efficiency, 2, 'm³/L');
            row.varieties = this.parseObject(row.varieties, 'varieties');
        },
        parseSeedingFields(row) {
            row.seeding_total = this.parseNumericField(row.seeding_total, 0, this.$t('AppWorkAnalyzerTable.sementes'));
            row.target_total = this.parseNumericField(row.target_total, 0, this.$t('AppWorkAnalyzerTable.sementes'));
            row.seeding_rate = this.parseNumericField(row.seeding_rate, 2, this.$t('AppWorkAnalyzerTable.sementes/ha'));
            row.target_rate = this.parseNumericField(row.target_rate, 2, this.$t('AppWorkAnalyzerTable.sementes/ha'));
            row.varieties = this.parseObject(row.varieties, 'varieties');
        },
        parseDates(label) {
            let start_end = label.split(' - ');
            let start = this.dateFormat(new Date(start_end[0] * 1000) / 1000, 'GMT');
            let end = this.dateFormat(new Date(start_end[1] * 1000) / 1000, 'GMT');
            return `${start} - ${end}`;
        },
        drillIn(drillInObject) {
            this.$emit('drillInFilter', drillInObject);
        },
        goToFieldAnalysis(fieldId, operationId) {
            let query = {org_id: this.selectedOrg.id, field_id: fieldId}

            if (operationId) {
                query.operation_id = operationId;
            }

            this.$router.push({
                name: 'agriculture-operations',
                query: query
            });
        },
        setPivot(pivot) {
            this.$emit('pivotSelected', pivot);
        },
        exportCSV() {
            let csvData = [];
            let jsonData = JSON.parse(JSON.stringify(this.formattedData));

            jsonData.forEach(item => {
                item.fieldsLabel = (item.end_date || this.pivot !== 'fields') ? item.fields.label : this.$t('AppWorkAnalyzerTable.Totais/Médias');
                item.vehiclesLabel = (item.end_date || this.pivot !== 'vehicles') ? item.vehicles.label : this.$t('AppWorkAnalyzerTable.Totais/Médias');
                item.operationsLabel = (item.end_date || this.pivot !== 'operations') ? item.operations.label : this.$t('AppWorkAnalyzerTable.Totais/Médias');
                item.operatorsLabel = (item.end_date || this.pivot !== 'operators') ? item.operators.label : this.$t('AppWorkAnalyzerTable.Totais/Médias');

                if (item.products) {
                    item.productsLabel = (item.end_date || this.pivot !== 'products') ? item.products.label : this.$t('AppWorkAnalyzerTable.Totais/Médias');
                }
                if (item.varieties) {
                    item.varietiesLabel = (item.end_date || this.pivot !== 'varieties') ? item.varieties.label : this.$t('AppWorkAnalyzerTable.Totais/Médias');
                }
                csvData.push(item);
            })

            const CSV_LABELS = [this.$t(`AppWorkAnalyzerTable.${this.pivot} (s)`), this.$t('AppWorkAnalyzerTable.Área'),
                this.$t('AppWorkAnalyzerTable.Produtividade'), this.translateLabel('time') + ' (h)',
                this.$t('AppWorkAnalyzerTable.Combustível total'), this.$t('AppWorkAnalyzerTable.Combustível por hora'),
                this.$t('AppWorkAnalyzerTable.Combustível por área'), this.$t('AppWorkAnalyzerTable.Velocidade'),
                this.translateLabel('end_date')];
            const CSV_FIELDS = [`${this.pivot}Label`, 'area', 'productivity', 'time_decimal', 'fuel', 'fuel_rate',
                'fuel_productivity', 'speed', 'end_date'];

            this.setOperationFields(CSV_FIELDS, CSV_LABELS);

            if (this.pivot !== 'fields') {
                CSV_LABELS.push(this.$t('AppWorkAnalyzerTable.fields (s)'));
                CSV_FIELDS.push('fieldsLabel');
            }

            if (this.pivot !== 'vehicles') {
                CSV_LABELS.push(this.$t('AppWorkAnalyzerTable.vehicles (s)'));
                CSV_FIELDS.push('vehiclesLabel');
            }

            if (this.pivot !== 'operations') {
                CSV_LABELS.push(this.$t('AppWorkAnalyzerTable.operations (s)'));
                CSV_FIELDS.push('operationsLabel');
            }

            if (this.pivot !== 'operators') {
                CSV_LABELS.push(this.$t('AppWorkAnalyzerTable.operators (s)'));
                CSV_FIELDS.push('operatorsLabel');
            }

            this.exportJsonToCsv(CSV_FIELDS, CSV_LABELS, csvData, this.fileName);
        },
        setOperationFields(CSV_FIELDS, CSV_LABELS) {
            switch (this.operationType.name) {
                case 'DRY_APPLICATION':
                case 'LIQUID_APPLICATION':
                    CSV_LABELS.push(this.$t('AppWorkAnalyzerApplicationTable.Taxa aplicada'),
                        this.$t('AppWorkAnalyzerApplicationTable.Total aplicado'),
                        this.$t('AppWorkAnalyzerApplicationTable.Taxa alvo'),
                        this.$t('AppWorkAnalyzerApplicationTable.Total alvo'));
                    CSV_FIELDS.push('average_rate', 'applied_total', 'target_rate', 'target_total');

                    if (this.operationType.name === 'LIQUID_APPLICATION') {
                        CSV_LABELS.push(this.$t('AppWorkAnalyzerApplicationTable.Pressão real'),
                            this.$t('AppWorkAnalyzerApplicationTable.Pressão alvo'));
                        CSV_FIELDS.push('actual_pressure', 'target_pressure');
                    }

                    if (this.pivot !== 'products') {
                        CSV_LABELS.push(this.$t('AppWorkAnalyzerTable.products (s)'));
                        CSV_FIELDS.push('productsLabel');
                    }
                    break;
                case 'MOWING':
                    if (this.pivot !== 'varieties') {
                        CSV_LABELS.push(this.$t('AppWorkAnalyzerTable.varieties (s)'));
                        CSV_FIELDS.push('varietiesLabel');
                    }
                    break;
                case 'HARVEST':
                    CSV_LABELS.push(this.$t('AppWorkAnalyzerHarvestTable.Umidade'),
                        this.$t('AppWorkAnalyzerHarvestTable.Produtividade total a seco'),
                        this.$t('AppWorkAnalyzerHarvestTable.Produtividade a seco'),
                        this.$t('AppWorkAnalyzerHarvestTable.Peso bruto total'),
                        this.$t('AppWorkAnalyzerHarvestTable.Peso líquido'),
                        this.$t('AppWorkAnalyzerHarvestTable.Rendimento seco'),
                        this.$t('AppWorkAnalyzerHarvestTable.Taxa de colheita'),
                        this.$t('AppWorkAnalyzerHarvestTable.Eficiência do combustível'));
                    CSV_FIELDS.push('average_moisture', 'total_dry_yield', 'average_dry_yield', 'total_wet_weight',
                        'average_wet_weight', 'throughput_dry', 'throughput_wet', 'fuel_efficiency');

                    if (this.pivot !== 'varieties') {
                        CSV_LABELS.push(this.$t('AppWorkAnalyzerTable.varieties (s)'));
                        CSV_FIELDS.push('varietiesLabel');
                    }
                    break;
                case 'SEEDING':
                    CSV_LABELS.push(this.$t('AppWorkAnalyzerSeedingTable.Plantio total'),
                        this.$t('AppWorkAnalyzerSeedingTable.Total alvo'),
                        this.$t('AppWorkAnalyzerSeedingTable.Taxa de plantio'),
                        this.$t('AppWorkAnalyzerSeedingTable.Taxa alvo'));
                    CSV_FIELDS.push('seeding_total', 'target_total', 'seeding_rate', 'target_rate');

                    if (this.pivot !== 'varieties') {
                        CSV_LABELS.push(this.$t('AppWorkAnalyzerTable.varieties (s)'));
                        CSV_FIELDS.push('varietiesLabel');
                    }
                    break;
                case 'TILLAGE':
                    CSV_LABELS.push(this.$t('AppWorkAnalyzerTillageTable.Profundidade alvo'),
                        this.$t('AppWorkAnalyzerTillageTable.Profundidade real'));
                    CSV_FIELDS.push('target_depth', 'actual_depth');
                    break;
                case 'TRANSPORT':
                    break;
            }
        },
        translateLabel(field) {
            switch (this.operationType.name) {
                case 'DRY_APPLICATION':
                case 'LIQUID_APPLICATION':
                    return field === 'time' ? this.$t('AppWorkAnalyzerApplicationTable.Tempo de aplicação') : this.$t('AppWorkAnalyzerApplicationTable.Última aplicação')
                case 'HARVEST':
                    return field === 'time' ? this.$t('AppWorkAnalyzerHarvestTable.Tempo de colheita') : this.$t('AppWorkAnalyzerHarvestTable.Última colheita')
                case 'MOWING':
                    return field === 'time' ? this.$t('AppWorkAnalyzerMowingTable.Tempo de roçagem') : this.$t('AppWorkAnalyzerMowingTable.Última roçagem')
                case 'SEEDING':
                    return field === 'time' ? this.$t('AppWorkAnalyzerSeedingTable.Tempo de plantio') : this.$t('AppWorkAnalyzerSeedingTable.Último plantio')
                case 'TILLAGE':
                    return field === 'time' ? this.$t('AppWorkAnalyzerTillageTable.Tempo de preparo') : this.$t('AppWorkAnalyzerTillageTable.Último preparo')
                case 'TRANSPORT':
                    return field === 'time' ? this.$t('AppWorkAnalyzerTransportTable.Tempo de transporte') : this.$t('AppWorkAnalyzerTransportTable.Último transporte')
            }
        },
        formatDate() {
            let formattedDate = this.dateFormat(new Date().getTime() / 1000, 'GMT')
            return formattedDate.replaceAll('/', '')
        }
    },
    props: {
        data: {
            type: Object
        },
        pivot: {
            type: String
        },
        drillInString: {
            type: String
        },
        operationType: {
            type: Object
        },
        tableView: {
            type: Object
        },
        loading: {
            type: Boolean
        },
        selectedOrg: {
            type: Object
        },
        allFiltersEmpty: {
            type: Boolean,
            default: false
        },
        origin: {
            type: String
        }
    },
    watch: {
        data: function () {
            if (!this.data) {
                return;
            }

            if (this.data.data.length === 0) {
                this.rows = {
                    data: [],
                    total: {}
                }
                return;
            }

            let tmpRows;
            try {
                tmpRows = {
                    data: this.formatRows(this.data.data),
                    total: this.formatRow(this.data.total)
                }
            } catch (e) {
                this.$emit('clearDataOnError')
                return;
            }

            this.rows = tmpRows;

            this.formattedData = JSON.parse(JSON.stringify(this.rows.data))
            this.formattedData.push(JSON.parse(JSON.stringify(this.rows.total)));
        }
    },
    computed: {
        isPdf: function () {
            return this.origin === 'pdf'
        },
        fileName: function () {
            return `${this.operationType.label}_${this.formatDate()}`
        }
    },
    components: {
        Skeleton,
        AppWorkAnalyzerApplicationTable,
        AppWorkAnalyzerHarvestTable,
        AppWorkAnalyzerSeedingTable,
        AppWorkAnalyzerTillageTable,
        AppWorkAnalyzerMowingTable,
        AppWorkAnalyzerTransportTable
    }
}
</script>

<style scoped>
.work-analyzer-table {
    max-width: 91.5vw;
}
</style>
